import type { Asset } from '../types/Asset';

// ─────────────────────────────────────────────────────────────────────────────
// Asset-type-driven field applicability — the SINGLE source of truth for the
// dynamic asset UI. Drives:
//   • AssetRecordPage form — non-applicable fields are HIDDEN for the current type.
//   • AssetsPage grid       — non-applicable cells are non-editable + shown blank
//                             (a grid column can't be hidden per-row).
//
// START STATE (per Nitzan, 05/07/2026): every field applies to EVERY asset type
// ('all'), so nothing is hidden yet. Tighten one field at a time by replacing
// 'all' with an explicit list of assetTypeIds — e.g.  callOrPut: [200, 201].
//
// Known option asset types (for convenience when writing rules):
//   200 אופציה CALL – מדד   201 אופציה PUT – מדד            (index options)
//   2008 אופציה על מניה     2014 …CALL   2015 …PUT           (stock options)
// ─────────────────────────────────────────────────────────────────────────────

type Applicability = 'all' | number[];

// Every editable asset field, listed explicitly so the whole matrix is visible
// in one place. assetId (PK) and assetTypeId (the driver itself) are always shown
// and intentionally omitted.
const FIELD_APPLICABILITY: Partial<Record<keyof Asset, Applicability>> = {
  assetName: 'all',
  shortName: 'all',
  ticker: 'all',
  symbol: 'all',
  isin: 'all',
  isin4Isa: 'all',
  baseAssetTypeId: 'all',
  creditRiskTypeId: 'all',
  isForeign: 'all',
  isGovernment: 'all',
  crcyId: 'all',
  linkageCrcyId: 'all',
  idxId: 'all',
  expDate: 'all',
  strike: 'all',
  callOrPut: 'all',
  underlyingAssetId: 'all',
  bloombergTradePlaceId: 'all',
  countryCode: 'all',
  benchmarkRateId: 'all',
  benchmarkRatePeriodId: 'all',
  isDynamicInterest: 'all',
  isPreMarketExchange: 'all',
  industryGroupId: 'all',
  industrySectorId: 'all',
  multiplier: 'all',
};

/** True if `field` should be shown/editable for an asset of `assetTypeId`. */
export function isAssetFieldApplicable(
  field: string,
  assetTypeId: number | null | undefined,
): boolean {
  const rule = FIELD_APPLICABILITY[field as keyof Asset];
  if (rule == null || rule === 'all') return true;
  if (assetTypeId == null) return true; // type not chosen yet → stay permissive
  return rule.includes(assetTypeId);
}
